import { addHours, addMinutes, subMinutes } from 'date-fns';
import { useDimensions } from 'hooks/useDimensions/useDimensions';
import { useOutletSite } from 'hooks/useOutletSite/useOutletSite';
import { usePlutoApiQuery } from 'hooks/useQuery/plutoApi';
import { fetchSiteStatesDataGapAndSubscribe } from 'pages/Operations/data';
import {
  GET_SITE_OPERATIONS_STATES,
  GET_SITE_OPERATIONS_SUMMARY,
} from 'pages/Operations/queries';
import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router';
import { useStore } from 'store';
import { findSite } from 'utils/site';

export const SiteOperations = () => {
  const [currentDate, hoveredDate, nowMode] = useStore((state) => [
    state.currentDate,
    state.hoveredDate,
    state.nowMode,
  ]);

  const dimensions = useDimensions();

  const { data: outletData } = useOutletSite();
  const { site: siteId } = useParams();

  const [breadCrumbSite, setBreadCrumbSite] = useState(
    findSite(outletData?.company?.sites, siteId),
  );

  const [breadCrumbLoading, setBreadCrumbLoading] = useState(false);

  const start = useMemo(
    () => (nowMode ? subMinutes(new Date(), 5) : currentDate),
    [currentDate, nowMode],
  );

  const queryVariables = useMemo(
    () => ({
      siteId: Number(siteId),
      start,
      end: nowMode ? addMinutes(start, 5) : addHours(start, 24),
    }),
    [siteId, start, nowMode],
  );

  const { data: siteSummary, loading: siteSummaryLoading } = usePlutoApiQuery(
    GET_SITE_OPERATIONS_SUMMARY,
    {
      variables: queryVariables,
      fetchPolicy: nowMode ? 'network-only' : 'cache-first',
    },
  );

  const {
    client: apolloClient,
    data,
    loading,
    subscribeToMore,
    fetchMore,
  } = usePlutoApiQuery(GET_SITE_OPERATIONS_STATES, {
    variables: queryVariables,
    fetchPolicy: nowMode ? 'network-only' : 'cache-first',
  });

  const site = useMemo(() => siteSummary?.site, [siteSummary]);
  const states = data?.site?.states;
  const isLoading = loading && siteSummaryLoading;

  useEffect(() => {
    if (!outletData?.company?.sites) {
      setBreadCrumbLoading(true);
    }
    if (outletData?.company?.sites && siteId) {
      setBreadCrumbLoading(false);
      setBreadCrumbSite(findSite(outletData?.company?.sites, siteId));
    }
  }, [outletData, siteId]);

  useEffect(() => {
    if (isLoading || !start || !siteId) {
      return;
    }

    let onUnsubscribe = () => {};

    (async () => {
      onUnsubscribe = await fetchSiteStatesDataGapAndSubscribe({
        apolloClient,
        queryVariables,
        fetchMore,
        subscribeToMore,
        nowMode,
      });
    })();

    return () => onUnsubscribe();
  }, [
    siteId,
    start,
    isLoading,
    queryVariables,
    fetchMore,
    subscribeToMore,
    nowMode,
  ]);

  if (!siteId) {
    return null;
  }

  return <div>Site Operations</div>;
};
